import { resolvePageBoxes } from "./boxes";
import type { SpreadAnchor } from "./binding";
import type {
  PublishingDocument,
  PublishingGeometry,
  PublishingGrid,
  PublishingGuide,
  PublishingPage,
} from "./document";
import { resolveMasterChain } from "./masters";

/** Resolved guide: `positionMm` is the one physical distance from the page's
 * TRIM top-left along the guide's axis. Inner/outer is already applied, so a
 * renderer must never mirror it again; `declaredMm`/`origin` are provenance. */
export type ResolvedGuide = {
  id: string;
  axis: "x" | "y";
  positionMm: number;
  declaredMm: number;
  origin?: "inner" | "outer";
  source: string;
};
export type ResolvedGrid = PublishingGrid & { source: string };

type Level = { guides?: PublishingGuide[]; grids?: PublishingGrid[] };

/** Physical offset of a declared guide position on a page side. */
export function physicalGuidePosition(
  guide: Pick<PublishingGuide, "axis" | "origin">,
  declared: number,
  side: "left" | "right",
  trimWidth: number,
) {
  if (guide.axis !== "x" || !guide.origin) return declared;
  const fromRight = (guide.origin === "inner") === (side === "left");
  return fromRight ? trimWidth - declared : declared;
}

/** Allowed range of a physical position relative to the trim origin, from the
 * resolved page boxes (negative values reach into bleed and slug). */
export function guideExtent(geometry: PublishingGeometry, axis: "x" | "y") {
  const { media, trim } = resolvePageBoxes(geometry, "left");
  return axis === "x"
    ? { min: media.x - trim.x, max: media.x + media.width - trim.x }
    : { min: media.y - trim.y, max: media.y + media.height - trim.y };
}

/** Guides/grids inherit by id: document -> master chain root -> leaf -> page.
 * A later level replaces an earlier entry with the same id (like master items);
 * duplicates within ONE container are refused by the schema. Page overrides
 * hide/move inherited guides only; nothing is copied into the page. */
export function resolvePageLayout(
  doc: PublishingDocument,
  page: Pick<
    PublishingPage,
    "id" | "masterId" | "guides" | "grids" | "guideOverrides"
  >,
  side: "left" | "right",
  geometry: PublishingGeometry,
): { guides: ResolvedGuide[]; grids: ResolvedGrid[] } {
  const resolvedGeometry = geometry;
  const levels: [string, Level][] = [
    ["document", doc],
    ...resolveMasterChain(doc, page.masterId).map(
      (master): [string, Level] => [`master:${master.id}`, master],
    ),
  ];
  const merged = new Map<string, ResolvedGuide & { raw: PublishingGuide }>();
  const grids = new Map<string, ResolvedGrid>();
  const place = (source: string, guide: PublishingGuide, declared: number) => {
    merged.set(guide.id, {
      id: guide.id,
      axis: guide.axis,
      declaredMm: declared,
      positionMm: physicalGuidePosition(
        guide,
        declared,
        side,
        resolvedGeometry.trim.width,
      ),
      ...(guide.origin ? { origin: guide.origin } : {}),
      source,
      raw: guide,
    });
  };
  for (const [source, level] of levels) {
    for (const guide of level.guides ?? []) place(source, guide, guide.position);
    for (const grid of level.grids ?? []) grids.set(grid.id, { ...grid, source });
  }
  for (const override of page.guideOverrides ?? []) {
    const target = merged.get(override.guideId);
    if (!target)
      throw new Error(
        `Page ${page.id} guide override targets unknown inherited guide ${override.guideId}`,
      );
    if (override.hidden) merged.delete(override.guideId);
    else if (override.position !== undefined)
      place(target.source, target.raw, override.position);
  }
  for (const guide of page.guides ?? []) place("page", guide, guide.position);
  for (const grid of page.grids ?? []) grids.set(grid.id, { ...grid, source: "page" });
  return {
    guides: [...merged.values()].map(({ raw: _raw, ...guide }) => guide),
    grids: [...grids.values()],
  };
}

export type ResolvedSpreadGuide = {
  id: string;
  axis: "x" | "y";
  /** From the spread's visual-left trim edge (x) / trim top (y); zero gutter. */
  positionMm: number;
  anchor: SpreadAnchor;
  /** Per-page offsets from each page's own trim origin. Display gap between
   * pages is a view concern and never enters these numbers. */
  placements: {
    pageId: string;
    axis: "x" | "y";
    offsetMm: number;
    source: string;
  }[];
};

export function resolveSpreadGuides(
  guides: NonNullable<PublishingDocument["spreadGuides"]>,
  spread: {
    left?: { id: string; geometry: PublishingGeometry };
    right?: { id: string; geometry: PublishingGeometry };
  },
  anchorOf: (guide: { id: string; anchor: SpreadAnchor }) => boolean,
): ResolvedSpreadGuide[] {
  return guides
    .filter(anchorOf)
    .map((guide) => {
      const leftWidth = spread.left?.geometry.trim.width ?? 0;
      const placements: ResolvedSpreadGuide["placements"] = [];
      for (const [slot, page] of [
        ["left", spread.left],
        ["right", spread.right],
      ] as const) {
        if (!page) continue;
        const offsetMm =
          guide.axis === "x" && slot === "right"
            ? guide.position - leftWidth
            : guide.position;
        const extent = guideExtent(page.geometry, guide.axis);
        if (offsetMm < extent.min || offsetMm > extent.max) continue;
        placements.push({
          pageId: page.id,
          axis: guide.axis,
          offsetMm,
          source: `spread:${guide.id}`,
        });
      }
      return {
        id: guide.id,
        axis: guide.axis,
        positionMm: guide.position,
        anchor: guide.anchor,
        placements,
      };
    });
}
